import type { ComposerAttributeNode, ComposerAttributeType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

const ATTRIBUTE_TYPE = 'Attribute' as ComposerAttributeType;
const GROUP_TYPE = 'AttributeGroup' as ComposerAttributeType;
const STEP_TYPE = 'AttributeStep' as ComposerAttributeType;
const PRESET_TYPE = 'PreSetContainer' as ComposerAttributeType;

export type ComposerAttributeDropPosition = 'before' | 'inside' | 'after';

const sameParent = (left?: string, right?: string) => (left || undefined) === (right || undefined);

const canContain = (parentType: ComposerAttributeType, childType: ComposerAttributeType) => {
    if (parentType === GROUP_TYPE) return childType === STEP_TYPE || childType === ATTRIBUTE_TYPE || childType === PRESET_TYPE;
    if (parentType === STEP_TYPE) return childType === ATTRIBUTE_TYPE || childType === PRESET_TYPE;
    return false;
};

const isDescendant = (nodesById: Map<string, ComposerAttributeNode>, ancestorId: string, candidate?: ComposerAttributeNode) => {
    let current = candidate;
    while (current?.parentID) {
        if (current.parentID === ancestorId) return true;
        current = nodesById.get(current.parentID);
    }
    return false;
};

const sortedSiblings = (attributes: ComposerAttributeNode[], parentID?: string, excludedId?: string) =>
    attributes
        .filter((candidate) => candidate.id !== excludedId && sameParent(candidate.parentID, parentID))
        .sort((left, right) => left.displayOrder - right.displayOrder || left.name.localeCompare(right.name));

export const moveComposerAttributeNode = (
    attributes: ComposerAttributeNode[],
    draggedId: string,
    targetId: string | null,
    requestedPosition?: ComposerAttributeDropPosition
): ComposerAttributeNode[] => {
    const nodesById = new Map(attributes.map((attribute) => [attribute.id, attribute]));
    const dragged = nodesById.get(draggedId);
    const target = targetId ? nodesById.get(targetId) : undefined;
    if (!dragged || (targetId && !target) || target?.id === dragged.id || isDescendant(nodesById, dragged.id, target)) return attributes;

    let destinationParent: ComposerAttributeNode | undefined;
    let destinationParentID: string | undefined;
    let destinationParentIDNumeric = -1;
    let destinationParentType: ComposerAttributeType | undefined;
    let insertBeforeId: string | undefined;

    const position = requestedPosition ?? (target && canContain(target.type, dragged.type) ? 'inside' : 'before');

    if (target && position === 'inside') {
        if (!canContain(target.type, dragged.type)) return attributes;
        destinationParent = target;
        destinationParentID = target.id;
        destinationParentIDNumeric = target.idNumeric;
        destinationParentType = target.type;
    } else if (target) {
        destinationParentID = target.parentID;
        destinationParentIDNumeric = target.parentIDNumeric;
        destinationParentType = target.parentType;
        destinationParent = destinationParentID ? nodesById.get(destinationParentID) : undefined;
        if (position === 'before') {
            insertBeforeId = target.id;
        } else {
            const targetSiblings = sortedSiblings(attributes, destinationParentID, dragged.id);
            const targetIndex = targetSiblings.findIndex((candidate) => candidate.id === target.id);
            insertBeforeId = targetSiblings[targetIndex + 1]?.id;
        }
    }

    if (destinationParent && !canContain(destinationParent.type, dragged.type)) return attributes;
    if (dragged.type === GROUP_TYPE && destinationParentID) return attributes;

    const sourceParentID = dragged.parentID;
    const destinationSiblings = sortedSiblings(attributes, destinationParentID, dragged.id);
    const insertIndex = insertBeforeId ? destinationSiblings.findIndex((candidate) => candidate.id === insertBeforeId) : destinationSiblings.length;
    if (insertBeforeId && insertIndex < 0) return attributes;

    const movedNode: ComposerAttributeNode = {
        ...dragged,
        parentID: destinationParentID,
        parentIDNumeric: destinationParentIDNumeric,
        parentType: destinationParentType
    };
    destinationSiblings.splice(insertIndex, 0, movedNode);

    const updates = new Map<string, ComposerAttributeNode>();
    destinationSiblings.forEach((candidate, displayOrder) => updates.set(candidate.id, { ...candidate, displayOrder }));

    if (!sameParent(sourceParentID, destinationParentID)) {
        sortedSiblings(attributes, sourceParentID, dragged.id).forEach((candidate, displayOrder) => updates.set(candidate.id, { ...candidate, displayOrder }));
    }

    return attributes.map((attribute) => updates.get(attribute.id) ?? attribute);
};
